import { toast } from 'react-toastify';

import { callApi } from '../../../../apiCaller';
import { asyncLocalStorage, toastError, tryParseJSON } from '../../../../utils';

import { setIsWaiting, setRemoveLoading, setShow, setShowRemovePopup } from '../../../../actions/general';
import {
     API_SUBSCRIBER_ADDRESS_DETAIL,
     API_SUBSCRIBER_ADDRESS,
     API_SUBSCRIBER_CREDIT_CARD_DELETE,
     API_SUBSCRIBER_CREDIT_CARD_ORG,
     API_SUBSCRIBER_CREDIT_CARD_UPDATE_ORG,
     API_SUBSCRIBER_CREDIT_CARD_UPDATE,
     API_SUBSCRIBER_CREDIT_CARD,
     CARD_TYPES,
     DEFAULT_FN,
     LOCAL_ORG_USER,
     POPUP_ADDRESS,
} from '../../../../constants';

import { setSubscriberUser } from '../GlobalStates/CurrentUser/actions';
import { setManagedOrganizations } from '../../Organizations/ManagedOrganizations/actions';
import { setOrganization } from '../../Organizations/Purchases/PaymentMethod/action';

const handleSetCreditCard = (setCreditCard, cardData) => {
     const {
          creditCardId,
          stripeCardId,
          stripeTestCardId,
          stripeCardType,
          stripeCardLast4Number,
          stripeCardExpiryMonth,
          stripeCardExpiryYear,
          name,
     } = cardData;

     const foundCardType = CARD_TYPES.find((item) => item.name === stripeCardType || item.name === 'Other');

     setCreditCard({
          creditCardId,
          stripeCardId,
          stripeTestCardId,
          stripeCardLast4Number,
          cardMask: foundCardType?.cardMask,
          cardImageSrc: foundCardType?.imageSrc,
          cardInputMask: foundCardType?.cardMask,
          stripeCardType,
          name,
          exp: `${stripeCardExpiryMonth < 10 ? '0' + stripeCardExpiryMonth : stripeCardExpiryMonth}/${stripeCardExpiryYear}`,
     });
};

// Address
export const addSubscriberAddress = (actionSubmit, addType, nextDispatch) => (dispatch) => {
     const { setActionSubmit, setAddType, setAddress } = nextDispatch;
     dispatch(setShow(POPUP_ADDRESS));
     setActionSubmit(actionSubmit);
     setAddType(addType);
     setAddress({
          country: '',
          street: '',
          city: '',
          state: '',
          zipcode: '',
     });
};

export const addSubscriberAddressRequest = (newValues) => (dispatch, getState) => {
     const user = getState().subscriber.user;

     dispatch(setIsWaiting(true));
     callApi(API_SUBSCRIBER_ADDRESS, 'POST', newValues)
          .then((response) => {
               if (response.status !== 200) {
                    toastError(response);
                    return;
               }

               const newAddress = response.data;
               const newUserAddresses = [...user.addresses];

               newUserAddresses.splice(newUserAddresses.length, 0, newAddress);
               const newUser = { ...user, addresses: newUserAddresses };

               dispatch(setSubscriberUser(newUser));
               dispatch(setShow(null));

               toast.success('Updated successfully');
          })
          .finally(() => {
               dispatch(setIsWaiting(false));
          });
};

export const updateSubscriberAddress = (actionSubmit, address, nextDispatch) => (dispatch) => {
     const { setActionSubmit, setAddress } = nextDispatch;
     dispatch(setShow(POPUP_ADDRESS));

     setActionSubmit(actionSubmit);
     setAddress(address);
};

export const updateSubscriberAddressRequest = (addressId, newAddress) => (dispatch, getState) => {
     const user = getState().subscriber.user;

     dispatch(setIsWaiting(true));
     callApi(API_SUBSCRIBER_ADDRESS_DETAIL.replace(':id', addressId), 'PUT', newAddress)
          .then((response) => {
               if (response.status !== 200) {
                    toastError(response);
                    return;
               }

               const newAddress = response.data;
               const newUser = { ...user };
               const newUserAddresses = newUser.addresses.map((item) => {
                    if (item.id === newAddress.id) {
                         return newAddress;
                    }

                    return item;
               });

               newUser.addresses = newUserAddresses;
               dispatch(setSubscriberUser(newUser));
               dispatch(setShow(null));

               toast.success('Updated successfully');
          })
          .finally(() => {
               dispatch(setIsWaiting(false));
          });
};

export const proceedRemoveSubscriberAddressRequest = (addressId) => (dispatch, getState) => {
     const user = getState().subscriber.user;

     dispatch(setRemoveLoading(true));
     callApi(API_SUBSCRIBER_ADDRESS_DETAIL.replace(':id', addressId), 'DELETE')
          .then((response) => {
               if (response.status !== 200) {
                    toastError(response);
                    return;
               }

               const userAddresses = user.addresses;
               if (userAddresses.length) {
                    const addressIndex = userAddresses.findIndex((address) => address.id === addressId);
                    userAddresses.splice(addressIndex, 1);
                    user.addresses = userAddresses;
                    dispatch(setSubscriberUser(user));
                    toast.success('Deleted successfully');
               }
          })
          .finally(() => {
               dispatch(setRemoveLoading(false));
               dispatch(setShowRemovePopup(null));
          });
};

// Payment Method
export const changeSubscriberCardNumber = (nextDispatch) => () => {
     const { event, setFieldValue, errors, setFieldError, setCard, resetCard } = nextDispatch;

     const cardNumberValue = event.target.value.replace(/_/gi, '').replace(/ /gi, '');

     if (cardNumberValue) {
          const cardTypeData = CARD_TYPES.filter((item) => {
               if (item.regexPrefix?.test(cardNumberValue)) {
                    return true;
               }
               return false;
          })[0];

          if (cardTypeData) {
               setCard({
                    cardType: cardTypeData.name,
                    cardInputMask: cardTypeData.inputMask,
                    cardInputPlaceholder: cardTypeData.inputPlaceholder,
                    cardCVCMask: cardTypeData.cvcMask,
                    cardCVCPlaceholder: cardTypeData.cvcPlaceholder,
                    cardMask: cardTypeData.cardMask,
                    cardImageSrc: cardTypeData.imageSrc,
               });
          } else {
               resetCard();
          }
     } else {
          resetCard();
     }

     setFieldValue('cvc', '');
     setFieldValue('number', cardNumberValue);

     if (errors['number']) {
          setFieldError('number', undefined);
     }
};

export const updateSubscriberCardNumberOnPaste = (nextDispatch) => () => {
     const { event, values, setFieldValue, errors, setFieldError, setCard, resetCard } = nextDispatch;

     let cardNumberValue = event.clipboardData.getData('Text');
     cardNumberValue = cardNumberValue.replace(/\D/g, '');
     const currentCardNumber = values.number.replace(/\D/g, '');

     if (currentCardNumber.length === cardNumberValue.length) {
          return;
     }

     event.preventDefault();

     const cardTypeData = CARD_TYPES.filter((item) => {
          if (item.regexPrefix?.test(cardNumberValue)) {
               return true;
          }
          return false;
     })[0];

     if (cardTypeData) {
          setCard({
               cardType: cardTypeData.name,
               cardInputMask: cardTypeData.inputMask,
               cardInputPlaceholder: cardTypeData.inputPlaceholder,
               cardCVCMask: cardTypeData.cvcMask,
               cardCVCPlaceholder: cardTypeData.cvcPlaceholder,
               cardMask: cardTypeData.cardMask,
               cardImageSrc: cardTypeData.imageSrc,
          });
          setFieldValue('cardInputMask', cardTypeData.inputMask);
          setFieldValue('cardImageSrc', cardTypeData.imageSrc);
          setFieldValue('cardInputPlaceholder', cardTypeData.inputPlaceholder);
     } else {
          resetCard();
     }

     setFieldValue('cvc', '');
     setFieldValue('number', cardNumberValue);

     if (errors['number']) {
          setFieldError('number', undefined);
     }
};

// Order
export const SET_SUBSCRIBER_ORDERS = 'setSubscriberOrders';

export const API_SUBSCRIBER_ORDER = 'order/user';

export const setSubscriberOrders = (orders) => {
     return {
          type: SET_SUBSCRIBER_ORDERS,
          payload: orders,
     };
};

export const fetchSubscriberOrderRequest =
     (setIsOrderLoading = DEFAULT_FN) =>
     (dispatch) => {
          callApi(API_SUBSCRIBER_ORDER, 'GET')
               .then((response) => {
                    if (response.status === 200) {
                         dispatch(setSubscriberOrders(response.data));
                    } else {
                         toastError(response);
                    }
               })
               .finally(() => {
                    setIsOrderLoading(false);
               });
     };

// Card User
export const createCreditCardUser = (userCard, nextDispatch) => (dispatch, getState) => {
     const user = getState().subscriber.user;
     if (!user?.id) return;

     if (!nextDispatch) nextDispatch = {};

     const { resetForm, setDisabledButtonSubmit = DEFAULT_FN, setSubmitting, setValidateOnChange } = nextDispatch;

     callApi(API_SUBSCRIBER_CREDIT_CARD.replace(':userId', user.id), 'POST', userCard).then((response) => {
          setSubmitting(false);
          setDisabledButtonSubmit(false);

          if (response.status !== 200) {
               toastError(response);
               return;
          }

          setValidateOnChange(false);
          resetForm();
          dispatch(setSubscriberUser(response.data));
          dispatch(setShow(null));

          if (nextDispatch.setCreditCard) {
               const { setCreditCard, setStepsData, setCurrentStep, setCurrentStep4, setCheckSelectPayment } = nextDispatch;
               const creditCards = response.data.creditCards || [];

               const latestCreditCard = creditCards[creditCards.length - 1];

               const cardData = {
                    creditCardId: latestCreditCard.id,
                    stripeCardId: latestCreditCard.stripeCardId ? latestCreditCard.stripeCardId : latestCreditCard.stripeTestCardId,
                    stripeTestCardId: latestCreditCard.stripeTestCardId,
                    stripeCardLast4Number: latestCreditCard.stripeCardLast4Number || latestCreditCard.stripeTestCardLast4Number,
                    stripeCardType: latestCreditCard.stripeCardType || latestCreditCard.stripeTestCardType,
                    name: latestCreditCard.stripeCardName || latestCreditCard.stripeTestCardName,
                    stripeCardExpiryMonth: latestCreditCard.stripeCardExpiryMonth || latestCreditCard.stripeTestCardExpiryMonth,
                    stripeCardExpiryYear: latestCreditCard.stripeCardExpiryYear || latestCreditCard.stripeTestCardExpiryYear,
               };

               handleSetCreditCard(setCreditCard, cardData);

               setStepsData();
               setCurrentStep4();
               setCurrentStep();
               setCheckSelectPayment();
          }

          toast.success('Payment method successfully created');
     });
};

export const updateCreditCardUser = (userCard, stripeCardId, nextDispatch) => (dispatch, getState) => {
     const user = getState().subscriber.user;
     const licensees = getState().subscriber.licensees;

     if (!user?.id) return;

     if (!nextDispatch) nextDispatch = {};

     const { resetForm, setDisabledButtonSubmit = DEFAULT_FN, setSubmitting, setValidateOnChange } = nextDispatch;

     callApi(API_SUBSCRIBER_CREDIT_CARD_UPDATE.replace(':userId', user.id).replace(':stripeCardId', stripeCardId), 'PUT', userCard).then(
          (response) => {
               setSubmitting(false);
               setDisabledButtonSubmit(false);

               if (response.status !== 200) {
                    toastError(response);
                    return;
               }

               setValidateOnChange(false);
               resetForm();
               dispatch(setSubscriberUser(response.data));
               dispatch(setShow(null));

               if (nextDispatch.setCreditCard) {
                    const { setCreditCard, setStepsData, setCurrentStep, setCurrentStep4, setCheckSelectPayment } = nextDispatch;

                    const creditCards = response.data.creditCards || [];

                    const newUserUpdated = creditCards.find((card) => card.stripeCardId === stripeCardId) || creditCards[creditCards.length - 1];

                    if (newUserUpdated) {
                         const licenseCurrent = licensees?.find((item) => item.country.includes(user?.country)) || {};
                         const isTestMode = !!licenseCurrent.testMode || !!user?.userTestMode;

                         const cardData = {
                              creditCardId: newUserUpdated.id,
                              stripeCardId: isTestMode ? newUserUpdated.stripeTestCardId : newUserUpdated.stripeCardId,
                              stripeTestCardId: isTestMode ? newUserUpdated.stripeTestCardId : undefined,
                              stripeCardLast4Number: isTestMode ? newUserUpdated.stripeTestCardLast4Number : newUserUpdated.stripeCardLast4Number,
                              stripeCardType: isTestMode ? newUserUpdated.stripeTestCardType : newUserUpdated.stripeCardType,
                              name: isTestMode ? newUserUpdated.stripeTestCardName : newUserUpdated.stripeCardName,
                              number: isTestMode ? newUserUpdated.stripeTestCardLast4Number : newUserUpdated.stripeCardLast4Number,
                              stripeCardExpiryMonth: isTestMode ? newUserUpdated.stripeTestCardExpiryMonth : newUserUpdated.stripeCardExpiryMonth,
                              stripeCardExpiryYear: isTestMode ? newUserUpdated.stripeTestCardExpiryYear : newUserUpdated.stripeCardExpiryYear,
                         };

                         handleSetCreditCard(setCreditCard, cardData);
                    }

                    setStepsData();
                    setCurrentStep4();
                    setCurrentStep();
                    setCheckSelectPayment();
               }

               toast.success('Payment method successfully updated');
          }
     );
};

export const deleteCreditCardUser = (cardId, setSelectedCardId) => (dispatch, getState) => {
     if (!cardId) {
          toast.error('Missing parameters!');
          return;
     }

     const user = getState().subscriber.user;
     dispatch(setRemoveLoading(true));

     callApi(API_SUBSCRIBER_CREDIT_CARD_DELETE.replace(':id', cardId), 'DELETE')
          .then((response) => {
               dispatch(setRemoveLoading(false));

               if (response.status !== 200) {
                    toastError(response);
                    return;
               }

               const userCreditCards = user.creditCards;
               if (userCreditCards.length) {
                    const cardIndex = userCreditCards.findIndex((card) => card.id === cardId);
                    userCreditCards.splice(cardIndex, 1);
                    user.creditCards = userCreditCards;
                    dispatch(setSubscriberUser(user));
               }

               toast.success('Deleted successfully');
          })
          .finally(() => {
               setSelectedCardId(false);
          });
};

// Card Org
export const createCreditCardOrg =
     (orgCard, organizationId, setValidateOnChange, resetForm, nextDispatch, setSubmitting = DEFAULT_FN) =>
     (dispatch, getState) => {
          if (!organizationId) {
               toast.error('Missing parameters!');
               return;
          }

          const managedOrganizations = getState().subscriber.managedOrganizations;
          const orgByUserCurrent = getState().subscriber.organization;

          if (!nextDispatch) nextDispatch = {};

          const { setDisabledButtonSubmit = DEFAULT_FN } = nextDispatch;

          callApi(API_SUBSCRIBER_CREDIT_CARD_ORG.replace(':organizationId', organizationId), 'POST', orgCard).then((response) => {
               setSubmitting(false);
               setDisabledButtonSubmit(false);

               if (response.status !== 200) {
                    toastError(response);
                    return;
               }

               const creditCards = response.data.creditCards || [];
               const creditCardId = creditCards[creditCards.length - 1].id;

               const newOrganization = {
                    ...orgByUserCurrent,
                    ...response.data,
               };
               const activeOrganizationOwner = tryParseJSON(localStorage.getItem(LOCAL_ORG_USER));
               activeOrganizationOwner.organization = newOrganization;

               asyncLocalStorage.setItem(LOCAL_ORG_USER, JSON.stringify(activeOrganizationOwner)).then(() => {
                    dispatch(setShow(null));
                    setValidateOnChange(false);
                    resetForm();

                    const newManagedOrganizations = managedOrganizations.map((item) => {
                         if (item.organizationId === organizationId) {
                              item.organization = newOrganization;
                         }
                         return item;
                    });
                    dispatch(setManagedOrganizations(newManagedOrganizations));
                    dispatch(setOrganization(newOrganization));

                    if (nextDispatch.setCreditCard) {
                         const { setCreditCard, setStepsData, setCurrentStep, setCurrentStep4, setCheckSelectPayment } = nextDispatch;

                         const newOrgUpdated = creditCards[creditCards.length - 1];

                         const cardData = {
                              creditCardId,
                              stripeCardId: newOrgUpdated.stripeCardId || newOrgUpdated.stripeTestCardId,
                              stripeTestCardId: newOrgUpdated.stripeTestCardId,
                              stripeCardLast4Number: newOrgUpdated.stripeCardLast4Number || newOrgUpdated.stripeTestCardLast4Number,
                              stripeCardType: newOrgUpdated.stripeCardType || newOrgUpdated.stripeTestCardType,
                              name: newOrgUpdated.stripeCardName || newOrgUpdated.stripeTestCardName,
                              stripeCardExpiryMonth: newOrgUpdated.stripeCardExpiryMonth || newOrgUpdated.stripeTestCardExpiryMonth,
                              stripeCardExpiryYear: newOrgUpdated.stripeCardExpiryYear || newOrgUpdated.stripeTestCardExpiryYear,
                         };

                         if (newOrgUpdated.stripeCardId) {
                              cardData.stripeCardId = newOrgUpdated.stripeCardId;
                         }

                         handleSetCreditCard(setCreditCard, cardData);

                         setStepsData();
                         setCurrentStep4();
                         setCurrentStep();
                         setCheckSelectPayment();
                    }

                    toast.success('Payment method successfully created');
               });
          });
     };

export const updateCreditCardOrg = (orgCard, organizationId, stripeCardId, setValidateOnChange, resetForm, nextDispatch) => (dispatch, getState) => {
     if (!organizationId) {
          toast.error('Missing parameters!');
          return;
     }

     const user = getState().subscriber.user;
     const licensees = getState().subscriber.licensees;
     const managedOrganizations = getState().subscriber.managedOrganizations;
     const orgByUserCurrent = getState().subscriber.organization;

     if (!nextDispatch) nextDispatch = {};

     const { stripeTestCardId, setDisabledButtonSubmit = DEFAULT_FN, setSubmitting = DEFAULT_FN } = nextDispatch;
     const organization = managedOrganizations?.find((item) => item.organizationId === organizationId)?.organization;

     callApi(
          API_SUBSCRIBER_CREDIT_CARD_UPDATE_ORG.replace(':organizationId', organizationId).replace(':stripeCardId', stripeCardId),
          'PUT',
          orgCard
     ).then((response) => {
          setSubmitting(false);
          setDisabledButtonSubmit(false);

          if (response.status !== 200) {
               toastError(response);
               return;
          }

          const creditCards = response.data.creditCards || [];
          const creditCardID = creditCards.find((creditCard) => creditCard.stripeCardId === stripeCardId)?.id;

          const newOrganization = {
               ...orgByUserCurrent,
               ...response.data,
          };
          const activeOrganizationOwner = tryParseJSON(localStorage.getItem(LOCAL_ORG_USER));
          activeOrganizationOwner.organization = newOrganization;

          asyncLocalStorage.setItem(LOCAL_ORG_USER, JSON.stringify(activeOrganizationOwner)).then(() => {
               dispatch(setShow(null));
               setValidateOnChange(false);
               resetForm();

               const newManagedOrganizations = managedOrganizations.map((item) => {
                    if (item.organizationId === organizationId) {
                         item.organization = newOrganization;
                    }
                    return item;
               });

               dispatch(setManagedOrganizations(newManagedOrganizations));
               dispatch(setOrganization(newOrganization));

               if (nextDispatch.setCreditCard) {
                    const { setCreditCard, setStepsData, setCurrentStep, setCurrentStep4, setCheckSelectPayment } = nextDispatch;

                    let newOrgUpdated = creditCards[creditCards.length - 1];
                    if (stripeCardId) {
                         const _newOrgUpdated = creditCards.find((card) => card.stripeCardId === stripeCardId);
                         _newOrgUpdated && (newOrgUpdated = _newOrgUpdated);
                    }

                    if (stripeTestCardId) {
                         const _newOrgUpdated = creditCards.find((card) => card.stripeTestCardId === stripeTestCardId);
                         _newOrgUpdated && (newOrgUpdated = _newOrgUpdated);
                    }

                    const licenseCurrent = licensees?.find((item) => item?.country.includes(organization?.country)) || {};
                    const isTestMode = !!licenseCurrent.testMode || !!user?.userTestMode;

                    const cardData = {
                         creditCardId: isTestMode ? newOrgUpdated.id : creditCardID,
                         stripeCardId: isTestMode ? newOrgUpdated.stripeTestCardId : newOrgUpdated.stripeCardId,
                         stripeTestCardId: isTestMode ? newOrgUpdated.stripeTestCardId : undefined,
                         stripeCardLast4Number: isTestMode ? newOrgUpdated.stripeTestCardLast4Number : newOrgUpdated.stripeCardLast4Number,
                         stripeCardType: isTestMode ? newOrgUpdated.stripeTestCardType : newOrgUpdated.stripeCardType,
                         name: isTestMode ? newOrgUpdated.stripeTestCardName : newOrgUpdated.stripeCardName,
                         stripeCardExpiryMonth: isTestMode ? newOrgUpdated.stripeTestCardExpiryMonth : newOrgUpdated.stripeCardExpiryMonth,
                         stripeCardExpiryYear: isTestMode ? newOrgUpdated.stripeTestCardExpiryYear : newOrgUpdated.stripeCardExpiryYear,
                    };

                    handleSetCreditCard(setCreditCard, cardData);

                    setStepsData();
                    setCurrentStep4();
                    setCurrentStep();
                    setCheckSelectPayment();
               }

               toast.success('Payment method successfully updated');
          });
     });
};

export const deleteCreditCardOrg = (cardId, setSelectedCardId, organizationId) => (dispatch, getState) => {
     if (!cardId) {
          toast.error('Missing parameters!');
          return;
     }

     const managedOrganizations = getState().subscriber.managedOrganizations;
     const orgByUserCurrent = getState().subscriber.organization;

     dispatch(setRemoveLoading(true));

     callApi(API_SUBSCRIBER_CREDIT_CARD_DELETE.replace(':id', cardId), 'DELETE')
          .then((response) => {
               dispatch(setRemoveLoading(false));

               if (response.status !== 200) {
                    toastError(response);
                    return;
               }

               const newCreditCards = orgByUserCurrent.creditCards.filter((card) => card.id !== cardId);
               const newOrganization = {
                    ...orgByUserCurrent,
                    creditCards: newCreditCards,
               };
               const activeOrganizationOwner = tryParseJSON(localStorage.getItem(LOCAL_ORG_USER));
               activeOrganizationOwner.organization = newOrganization;

               asyncLocalStorage.setItem(LOCAL_ORG_USER, JSON.stringify(activeOrganizationOwner)).then(() => {
                    const newManagedOrganizations = managedOrganizations.map((item) => {
                         if (item.organizationId === organizationId) {
                              item.organization = newOrganization;
                         }
                         return item;
                    });

                    dispatch(setManagedOrganizations(newManagedOrganizations));
                    dispatch(setOrganization(newOrganization));

                    toast.success('Deleted successfully');
               });
          })
          .finally(() => {
               setSelectedCardId(false);
          });
};
